import React, { useEffect, useMemo } from "react";
import cx from "classnames";
import styles from "../../ProductConfigurationResultMainView.module.scss";
import { ExpandableSection } from "../../ExpandableSection";
import { useTranslate } from "hooks";
import { ProductAddonSuggestions } from "./ProductAddonSuggestions/ProductAddonSuggestions";
import { useResultingListForm } from "../providers/ResultingListProvider";
import { SelectStrategyInput } from "../../SingleSelectionTab/components/SubComponents/SelectStrategyInput";
import type { ListViewSectionStrict } from "stores/product";
import { useProductOutput } from "stores/product";
import { ResultingListProperties } from "./ResultingListProperties";
import { SuggestionTable } from "./ResultingListTable/SuggestionTable";

export const ResultingList = () => {
  const translate = useTranslate();
  const productOutput = useProductOutput();
  const listViewConfiguration = productOutput?.product?.listViewConfiguration;

  const { setInitValues } = useResultingListForm();

  const attachments = useMemo(
    () =>
      (listViewConfiguration?.attachments &&
        listViewConfiguration?.attachments.filter(
          (attachment) => attachment !== null,
        )) ??
      [],
    [listViewConfiguration?.attachments],
  );

  const accessories = useMemo(
    () =>
      (listViewConfiguration?.accessories &&
        listViewConfiguration?.accessories.filter(
          (accessory) => accessory !== null,
        )) ??
      [],
    [listViewConfiguration?.accessories],
  );

  useEffect(() => {
    if (!attachments.length && !accessories.length) {
      return;
    }

    const initialValues = extractInitialValues([
      ...attachments,
      ...accessories,
    ]);

    setInitValues(initialValues);
  }, [setInitValues, attachments, accessories]);

  return (
    <div className={cx(styles.contentCard, "d-flex flex-column h-100")}>
      <ExpandableSection
        title={translate("PRODUCT_SUGGESTION_INPUT")}
        classNames={cx(
          styles.acousticCell,
          "border rounded p-1 m-2 m-xxl-3 mt-2 mt-xxl-3",
        )}
      >
        <SelectStrategyInput />
        <div className="px-4">
          <ResultingListProperties />
        </div>
      </ExpandableSection>
      {Boolean(attachments.length || accessories.length) && (
        <ExpandableSection
          title={translate("PRODUCT_ADD_ON")}
          classNames={cx(
            styles.acousticCell,
            "border rounded pt-2 m-2 m-xxl-3 mt-xxl-0 px-1",
          )}
        >
          <div className="px-4 mt-2">
            <ProductAddonSuggestions />
          </div>
        </ExpandableSection>
      )}
      <SuggestionTable />
    </div>
  );
};

export type Section = ListViewSectionStrict;

export const extractInitialValues = (sections: Section[]) => {
  const values: Record<string, boolean> = {};

  sections?.forEach((section) => {
    section?.groups?.forEach((group) => {
      group?.controls?.forEach((control) => {
        if (control?.boolValue && control?.id) {
          values[control.id] = true;
        }
      });
    });
  });

  return values;
};
